import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
    Box,
    Stack,
    IconButton,
    Tooltip,
    Divider
} from '@mui/material';

import { IoMdRefresh } from 'react-icons/io';

import { Icon as Iconify } from '@iconify/react';
import ModalPopUp from '../../../commons/ModalPopup';
import { renderControls } from '../../RetailerProductWiseScheme/utils/renderControls';

import RetailerWiseProductWiseSchemeService from '../../RetailerProductWiseScheme/RetailerWiseProductWiseSchemeService';
import LoadingProgressBar from '../../../components/loading-progressbar/loadingprogressbar';
import { useAppStore } from '../../../store/appStore';


export default function RetailerWiseProductWiseSchemeFilterSearch({ open, onClose, onError }) {

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        reset

    } = useForm();

    const values = watch();
    const showInvDetailsValue = watch('ShowInvDetails');
    const {
        searchControls, setSearchControls,
        setFilterControlValues, filterControlValues,
        setHoverFilterValues, setFilteredData, filteredData,
        setFilterPayload
    } = useAppStore();

    const [loading, setLoading] = useState(false)
    const [initialSearchControls, setInitialSearchControls] = useState([]);
    const [hasCapturedInitial, setHasCapturedInitial] = useState(false);
    const [preProcessSchemeGroups, setPreProcessSchemeGroups] = useState([]);
    const [preProcessSchemeNames, setPreProcessSchemeNames] = useState([]);




    const isResetting = useRef(false);
    const isFirstLoadAfterOpen = useRef(true);
    const lastFetchedGroupId = useRef(null);
    const lastFetchedSchemes = useRef(null);
    const hasInitializedRef = useRef(false);
    const prevReportGeneratedOn = useRef(null);
    const cachedPreProcessSchemeGroups = useRef(null);

    // Default value for the "Report Filter" dropdown — always kept selected
    const DEFAULT_REPORT_FILTER = { ReportFilterId: 1, ReportFilter: "Distributor wise" };
     console.log('values :- ', values);
    console.log('searchControls :- ', searchControls);

    useEffect(() => {
        if (open) {
            isFirstLoadAfterOpen.current = true;
        } else {
            hasInitializedRef.current = false;
            prevReportGeneratedOn.current = null;
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            hasInitializedRef.current = false;
            return;
        }

        if (hasInitializedRef.current) {
            return; // Prevent resetting values when searchControls changes dynamically
        }

        if (searchControls && searchControls.length > 0) {
            isResetting.current = true;
            if (filterControlValues && Object.keys(filterControlValues).length > 0) {
                // Ensure ReportFilterId always has a value when restoring saved filters
                reset({
                    ...filterControlValues,
                    ReportFilterId: filterControlValues.ReportFilterId || DEFAULT_REPORT_FILTER,
                });
            } else {
                // Populate default values from searchControls dynamically
                const defaultVals = { ReportFilterId: DEFAULT_REPORT_FILTER };
                searchControls.forEach(ctrl => {
                    const name = ctrl?.ValueExpr;
                    if (name === "SchemeGroup" || name === "SchemeGroupId") {
                        return; // Skip setting initial value for Scheme Group to avoid auto API call
                    }
                    if (name && ctrl.DefaultValues !== null && ctrl.DefaultValues !== undefined) {
                        if (name === "ShowInvDetails") {
                            defaultVals[name] = false;
                        } else if (ctrl.DataType === "RadioButton") {
                            const defVal = Array.isArray(ctrl.DefaultValues) ? ctrl.DefaultValues[0] : ctrl.DefaultValues;
                            defaultVals[name] = defVal;
                        } else if (ctrl.DataType === "MultiselectDropdown") {
                            if (name === "SchemeName") {
                                defaultVals[name] = []; // Do not pre-select all schemes by default
                            } else {
                                defaultVals[name] = Array.isArray(ctrl.DefaultValues) ? ctrl.DefaultValues : [];
                            }
                        } else if (ctrl.DataType === "Dropdown") {
                            defaultVals[name] = Array.isArray(ctrl.DefaultValues) ? ctrl.DefaultValues[0] : ctrl.DefaultValues;
                        } else {
                            defaultVals[name] = ctrl.DefaultValues;
                        }
                    }
                });
                reset(defaultVals); // Reset to defaults
            }
            hasInitializedRef.current = true;
            setTimeout(() => {
                isResetting.current = false;
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, searchControls]);

    // Guard: if the user clears the Report Filter dropdown, snap it back to the default
    const reportFilterValue = watch('ReportFilterId');
    useEffect(() => {
        if (!reportFilterValue && !isResetting.current) {
            setValue('ReportFilterId', DEFAULT_REPORT_FILTER);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportFilterValue]);


    const [serviceInstance, setServiceInstance] = useState(null);

    useEffect(() => {
        const setInstance = async () => {
            const instance = await RetailerWiseProductWiseSchemeService;
            setServiceInstance(instance);
            try {
                const res = await instance.GetPreProcessRPWSSchemeGroup();
                const groupData = typeof res === 'string' ? JSON.parse(res) : (res?.data || res);
                const groups = Array.isArray(groupData?.SchemeGroup) ? groupData.SchemeGroup : [];
                const schemes = Array.isArray(groupData?.SchemeName) ? groupData.SchemeName : [];
                setPreProcessSchemeGroups(groups);
                setPreProcessSchemeNames(schemes);
                cachedPreProcessSchemeGroups.current = groups;
                console.log("GetPreProcessRPWSSchemeGroup result groups:", groups);
                console.log("GetPreProcessRPWSSchemeGroup result schemes:", schemes);
            } catch (err) {
                console.error("Error calling GetPreProcessRPWSSchemeGroup:", err);
            }
        };
        setInstance();
    }, []);

    // Capture initial controls for reverting
    useEffect(() => {
        console.log("@@ Report Filter open:", open);
        if (searchControls && searchControls.length > 0 && !hasCapturedInitial) {
            console.log("@@ Capturing initial search controls");
            setInitialSearchControls(searchControls);
            setHasCapturedInitial(true);
        }
    }, [searchControls, hasCapturedInitial, open]);

    const reportGeneratedOnValue = watch('ReportGeneratedOn');

    const getActiveGroups = () => {
        const val = reportGeneratedOnValue !== undefined && reportGeneratedOnValue !== null 
            ? String(reportGeneratedOnValue) 
            : "1";
        if (val === "0") {
            return preProcessSchemeGroups;
        } 
        const initialGroupControl = initialSearchControls.find(ctrl => 
            ctrl.LabelName === "Scheme Group" || 
            ctrl.ValueExpr === "SchemeGroup" || 
            ctrl.ValueExpr === "SchemeGroupId"
        );
        return initialGroupControl?.DefaultValues || [];
    };

    const watchedSchemeGroup = watch('SchemeGroup');
    const watchedSchemeGroupId = watch('SchemeGroupId');
    const schemeGroup = watchedSchemeGroup || watchedSchemeGroupId;

    useEffect(() => {
        const updateSchemeOptions = async () => {
            console.log("@@ Report Scheme Group Changed:", schemeGroup);
            const currentSearchControls = useAppStore.getState().searchControls;
            if (!Array.isArray(currentSearchControls)) {
                return;
            }
            const schemeControl = currentSearchControls.find(ctrl =>
                ctrl.LabelName === "SchemeName" ||
                ctrl.ValueExpr === "SchemeName" ||
                ctrl.ValueExpr === "SchemeId" ||
                (ctrl.DataType === "MultiselectDropdown" && ctrl.ValueExpr !== "DistributorId")
            );
            const schemeValueExpr = schemeControl?.ValueExpr || "SchemeName";

            if (!schemeGroup) {
                console.log("@@ Report: Reverting to initial controls");
                lastFetchedGroupId.current = null;
                // RESTORE FROM INITIAL CONTROLS
                if (initialSearchControls.length > 0) {
                    const activeGroups = getActiveGroups();
                    const isPreProcessVal = (reportGeneratedOnValue !== undefined && reportGeneratedOnValue !== null ? String(reportGeneratedOnValue) : "1") === "0";
                    const updated = initialSearchControls.map(ctrl => {
                        if (ctrl.LabelName === "Scheme Group" || ctrl.ValueExpr === "SchemeGroup" || ctrl.ValueExpr === "SchemeGroupId") {
                            return {
                                ...ctrl,
                                DefaultValues: activeGroups
                            };
                        }
                        if (ctrl.ValueExpr === schemeValueExpr) {
                            return {
                                ...ctrl,
                                DefaultValues: isPreProcessVal ? preProcessSchemeNames : (ctrl.DefaultValues || [])
                            };
                        }
                        return ctrl;
                    });
                    setSearchControls(updated);
                }
                if (isFirstLoadAfterOpen.current) {
                    // Do not overwrite restored selections
                } else {
                    setValue(schemeValueExpr, []);
                }
                return;
            }

            // MAPPING STRING NAME TO ID
            const groupControl = currentSearchControls.find(c => (c.LabelName === "Scheme Group" || c.ColumnName === "SchemeGroup"));
            console.log("@@ Found Group Control:", groupControl);

            // The value might be a string (from ValueExpr: SchemeGroup) or ID
            const selectedValue = typeof schemeGroup === 'object' ? (schemeGroup?.SchemeGroupId || schemeGroup?.Id) : schemeGroup;
            console.log("@@ Selected Value:", selectedValue);

            const selectedGroup = groupControl?.DefaultValues?.find(v => (v.SchemeGroup === selectedValue || v.SchemeGroupId === selectedValue || v.Id === selectedValue));
            const groupId = selectedGroup?.SchemeGroupId || (typeof selectedValue === 'number' ? selectedValue : null);

            console.log("@@ Determined Group ID:", groupId);

            if (groupId) {
                // If this group was already fetched and the current schemeControl has the correct DefaultValues, skip
                const isAlreadySet = schemeControl?.DefaultValues &&
                    lastFetchedSchemes.current &&
                    schemeControl.DefaultValues.length === lastFetchedSchemes.current.length &&
                    schemeControl.DefaultValues.every((opt, i) => opt.SchemeId === lastFetchedSchemes.current[i]?.SchemeId);

                if (groupId === lastFetchedGroupId.current && isAlreadySet) {
                    return;
                }

                try {
                    console.log("@@ Report calling API for Group:", groupId);
                    setLoading(true);
                    // fetch schemes (isRegeneration=false, isReport=true)
                    const isPreProcessVal = (reportGeneratedOnValue !== undefined && reportGeneratedOnValue !== null ? String(reportGeneratedOnValue) : "1") === "0";

                    const res = await serviceInstance.getSchemesByGroupId(groupId, false, true, false, isPreProcessVal);
                    const schemesData = typeof res === 'string' ? JSON.parse(res) : (res?.data || res);
                    const schemes = Array.isArray(schemesData) ? schemesData : [];
                    console.log("@@ Report fetched schemes:", schemes);

                    lastFetchedGroupId.current = groupId;
                    lastFetchedSchemes.current = schemes;

                    if (schemes) {
                        const schemeDataType = schemeControl?.DataType;

                        const updatedControls = currentSearchControls.map(ctrl => {
                            if (ctrl.ValueExpr === schemeValueExpr) {
                                return {
                                    ...ctrl,
                                    DefaultValues: schemes
                                };
                            }
                            return ctrl;
                        });
                        setSearchControls(updatedControls);

                        // Always select all schemes when a group is selected
                        if (schemeDataType === "MultiselectDropdown") {
                            setValue(schemeValueExpr, schemes);
                        } else {
                            setValue(schemeValueExpr, schemes.length === 1 ? schemes[0] : null);
                        }
                        isFirstLoadAfterOpen.current = false;
                    }
                } catch (err) {
                    console.error("@@ Error fetching dependent schemes:", err);
                } finally {
                    setLoading(false);
                }
            } else {
                console.log("@@ No Group ID found for selection");
            }
        };

        if (serviceInstance && open) {
            updateSchemeOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schemeGroup, serviceInstance, open, initialSearchControls, preProcessSchemeGroups, preProcessSchemeNames, reportGeneratedOnValue]);

    useEffect(() => {
        const handleReportGeneratedOnCallback = () => {
            if (!serviceInstance || !open || !hasCapturedInitial || initialSearchControls.length === 0) {
                return;
            }

            const currentSearchControls = useAppStore.getState().searchControls;
            if (!Array.isArray(currentSearchControls)) {
                return;
            }

            const val = reportGeneratedOnValue !== undefined && reportGeneratedOnValue !== null 
                ? String(reportGeneratedOnValue) 
                : "1";

            const schemeControl = initialSearchControls.find(ctrl =>
                ctrl.LabelName === "SchemeName" ||
                ctrl.ValueExpr === "SchemeName" ||
                ctrl.ValueExpr === "SchemeId" ||
                (ctrl.DataType === "MultiselectDropdown" && ctrl.ValueExpr !== "DistributorId")
            );
            const schemeValueExpr = schemeControl?.ValueExpr || "SchemeName";

            // If the value changed dynamically, reset form selections
            if (prevReportGeneratedOn.current !== null && prevReportGeneratedOn.current !== val) {
                setValue('SchemeGroup', null);
                setValue('SchemeGroupId', null);
                setValue(schemeValueExpr, []);
            }

            if (val === "0") {
                const groups = preProcessSchemeGroups;
                const schemes = preProcessSchemeNames;
                console.log("Using cached PreProcess scheme groups:", groups);

                const groupControl = currentSearchControls.find(ctrl => 
                    ctrl.LabelName === "Scheme Group" || 
                    ctrl.ValueExpr === "SchemeGroup" || 
                    ctrl.ValueExpr === "SchemeGroupId"
                );
                const currentGroups = groupControl?.DefaultValues || [];
                const isGroupsAlreadyEqual = currentGroups.length === groups.length &&
                    currentGroups.every((g, i) => (g.SchemeGroupId || g.Id || g.SchemeGroup) === (groups[i]?.SchemeGroupId || groups[i]?.Id || groups[i]?.SchemeGroup));

                const currentSchemes = schemeControl?.DefaultValues || [];
                const isSchemesAlreadyEqual = currentSchemes.length === schemes.length &&
                    currentSchemes.every((s, i) => s.SchemeId === schemes[i]?.SchemeId);

                if (!isGroupsAlreadyEqual || !isSchemesAlreadyEqual) {
                    const updated = currentSearchControls.map(ctrl => {
                        if (ctrl.LabelName === "Scheme Group" || ctrl.ValueExpr === "SchemeGroup" || ctrl.ValueExpr === "SchemeGroupId") {
                            return {
                                ...ctrl,
                                DefaultValues: groups
                            };
                        }
                        if (ctrl.ValueExpr === schemeValueExpr) {
                            return {
                                ...ctrl,
                                DefaultValues: schemes
                            };
                        }
                        return ctrl;
                    });
                    setSearchControls(updated);
                }
            } else {
                // Restore default values from initialSearchControls
                console.log("Restoring default Scheme Group and Scheme Name values from initialSearchControls");
                const initialGroupControl = initialSearchControls.find(ctrl => 
                    ctrl.LabelName === "Scheme Group" || 
                    ctrl.ValueExpr === "SchemeGroup" || 
                    ctrl.ValueExpr === "SchemeGroupId"
                );
                const defaultGroups = initialGroupControl?.DefaultValues || [];

                const initialSchemeControl = initialSearchControls.find(ctrl => 
                    ctrl.ValueExpr === schemeValueExpr
                );
                const defaultSchemes = initialSchemeControl?.DefaultValues || [];

                // Guard: only update if different
                const groupControl = currentSearchControls.find(ctrl => 
                    ctrl.LabelName === "Scheme Group" || 
                    ctrl.ValueExpr === "SchemeGroup" || 
                    ctrl.ValueExpr === "SchemeGroupId"
                );
                const currentGroups = groupControl?.DefaultValues || [];
                const isGroupsAlreadyEqual = currentGroups.length === defaultGroups.length &&
                    currentGroups.every((g, i) => (g.SchemeGroupId || g.Id || g.SchemeGroup) === (defaultGroups[i]?.SchemeGroupId || defaultGroups[i]?.Id || defaultGroups[i]?.SchemeGroup));

                const currentSchemes = schemeControl?.DefaultValues || [];
                const isSchemesAlreadyEqual = currentSchemes.length === defaultSchemes.length &&
                    currentSchemes.every((s, i) => s.SchemeId === defaultSchemes[i]?.SchemeId);

                if (!isGroupsAlreadyEqual || !isSchemesAlreadyEqual) {
                    const updated = currentSearchControls.map(ctrl => {
                        if (ctrl.LabelName === "Scheme Group" || ctrl.ValueExpr === "SchemeGroup" || ctrl.ValueExpr === "SchemeGroupId") {
                            return {
                                ...ctrl,
                                DefaultValues: defaultGroups
                            };
                        }
                        if (ctrl.ValueExpr === schemeValueExpr) {
                            return {
                                ...ctrl,
                                DefaultValues: defaultSchemes
                            };
                        }
                        return ctrl;
                    });
                    setSearchControls(updated);
                }
            }

            prevReportGeneratedOn.current = val;
        };

        handleReportGeneratedOnCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportGeneratedOnValue, serviceInstance, open, hasCapturedInitial, initialSearchControls, preProcessSchemeGroups, preProcessSchemeNames]);




    const onSubmit = async (data) => {
        // console.log('searchControls', searchControls);
        console.log('data', data);

        //  Resolve ShowDetails once (no ternary nesting)
        const showDetailsValue = data?.ShowDetails === true ? "1" : "0";


        setFilterControlValues(data);

        try {
            setLoading(true);

            const schemeConversionFilterServiceInstance =
                await RetailerWiseProductWiseSchemeService;

            if (!schemeConversionFilterServiceInstance) {
                throw new Error("Service instance could not be created");
            }

            //  ALWAYS-BUILT PAYLOAD (fixed structure)
            // Start - Logic to handle "Select All" or "Empty Selection" as "All Schemes"
            const schemeControl = searchControls?.find(ctrl => ctrl?.DataType === "MultiselectDropdown");
            const totalSchemes = schemeControl?.DefaultValues?.length || 0;
            const selectedSchemes = data?.SchemeName || [];

            const distributorControl = searchControls?.find(
                ctrl => ctrl?.DataType === "MultiselectDropdown" && ctrl?.ValueExpr === "DistributorId"
            );

            const totalDistributors = distributorControl?.DefaultValues?.length || 0;
            const selectedDistributors = data?.DistributorId || [];

            const payload = {
                // SchemeIds: (selectedSchemes?.length > 0 && selectedSchemes?.length < totalSchemes)
                //    ? selectedSchemes.map(s => s?.SchemeId)
                //    : [],
                SchemeIds: selectedSchemes?.map(s => s?.SchemeId) || [],
                // DistributorIds: (selectedDistributors?.length > 0 && selectedDistributors?.length < totalDistributors)
                //    ? selectedDistributors.map(d => d?.DistributorId)
                //    : [],
                DistributorIds: selectedDistributors?.map(d => d?.DistributorId) || [],

                FromDate: data?.FromDate || "",
                ToDate: data?.ToDate || "",
                Description: data?.Description || "",
                ReportFilterId: (data?.ReportFilterId?.ReportFilterId ?? DEFAULT_REPORT_FILTER.ReportFilterId) || 1,
                ShowInvDetails: data?.ShowInvDetails !== undefined ? data.ShowInvDetails : true,
            };

            const grpId = data?.SchemeGroupId || data?.SchemeGroup;
            if (grpId) {
                payload.SchemeGroupId = typeof grpId === 'object' ? (grpId.SchemeGroupId || grpId.Id) : grpId;
            }

            const recCat = data?.RecordCategoriesId || data?.RecordCategories;
            if (recCat !== undefined && recCat !== null) {
                payload.SchemeEligible = String(typeof recCat === 'object' ? (recCat.RecordCategoriesId ?? recCat.Id ?? "") : recCat);
            }

            if (data?.ReportGeneratedOn !== undefined && data?.ReportGeneratedOn !== null) {
                payload.ReportGeneratedOn = Number(data.ReportGeneratedOn);
            }

            console.log('payload', payload);

            setHoverFilterValues(data);
            setFilterPayload(payload);

            const response =
                await schemeConversionFilterServiceInstance
                    .GetRetailerProductSchemeClaimReport(payload);
            console.log(response);

            if (response?.status === 200 && response?.data[0]?.Message === 'PRECLAIM DATA NOT GENERATED') {
                if (onError) {
                    onError({ errorCode: 500, errorMessage: response?.data[0]?.Message });
                }
            }

            console.log("response.data: ", response.data);
            setFilteredData(response?.data || []);
            onClose();
           
           
        } catch (err) {
            console.log('error', err);

            if (onError) {
                onError({
                    errorCode: err?.response?.data?.error?.code,
                    errorMessage: err?.response?.data?.error?.message
                });
            }

            if (err?.response?.data?.error?.code === 404) {
                setFilteredData([]);
            }
        } finally {
            setLoading(false);
        }
    };




    const handleReset = () => {
        reset({
            SchemeName: [],
            DistributorId: [],
            FromDate: "",
            ToDate: "",
            Description: "",
            ShowDetails: 0,
            ShowInvDetails: false,
            SchemeGroup: null,
            SchemeGroupId: null,
            ReportGeneratedOn: null,
            ReportFilterId: DEFAULT_REPORT_FILTER, // Always reset to default
        });
        setFilterControlValues([]);
    };








    return (


        // eslint-disable-next-line react/jsx-boolean-value
        <ModalPopUp open={open} onClose={onClose} title='Filter'
        // sx={{ width: { xs: 320, sm: 520, md: 680 } }}
        >
            <form onSubmit={handleSubmit(onSubmit)}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: 0,

                }}
            >
                <Stack spacing={2} sx={{
                    pb: 1.3, pt: 0.1, display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto' // Allow scrolling when height exceeds viewport
                }}>
                    <Box
                        sx={{
                            overflow: "visible", // instead of "auto"
                            rowGap: 2,
                            columnGap: 2,
                            display: 'grid',
                            // gridTemplateColumns='repeat(4, 1fr)'
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr',
                                md: '1fr 1fr',
                                lg: '1fr 1fr ',
                                xl: '1fr 1fr 1fr'
                            },
                            gridAutoRows: 'min-content',
                          //  paddingTop: '0.4rem',
                            // border: '2px solid red',
                        }}
                    >
                        {searchControls?.map((field, index) => {
                            const isRadioButton = field.DataType === 'RadioButton';
                            return (
                                <Box
                                    key={field.ValueExpr || index}
                                    sx={{
                                        gridColumn: isRadioButton ? '1 / -1' : 'auto'
                                    }}
                                >
                                    {renderControls(field, index, control, serviceInstance?.SearchFilter)}
                                </Box>
                            );
                        })}
                    </Box>
                </Stack>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1,
                    px: 0.5
                }}>
                    <Box sx={{ fontSize: '13px', color: '#000' }}>
                        {showInvDetailsValue ? 'Please download report to check invoice details.' : ''}
                    </Box>
                    <Box>
                        <Tooltip title='Reset' sx={{ bgcolor: '#fdd835', color: '#fff', borderRadius: '4px', height: "34px", width: "34px", padding: "5px" }}>
                            <IconButton onClick={handleReset}>
                                <IoMdRefresh />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            title='Apply'
                            sx={{ bgcolor: '#43b5a2', color: '#fff', borderRadius: '4px', marginLeft: '8px', height: "34px", width: "34px", padding: "5px" }}
                        >
                            <IconButton type='submit'>
                                <Iconify icon='mdi:filter' />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </form>
            {loading && <LoadingProgressBar />}

        </ModalPopUp>

    );

}

RetailerWiseProductWiseSchemeFilterSearch.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onError: PropTypes.func,
};